<template>
  <loading-overlay v-if="loading" class="animated fadeIn" />
  <div v-else class="animated fadeIn">
    <default-heading class="mt-3">
      Customer Integration Details

      <template #right>
        <b-button
          variant="primary"
          class="ml-1"
          to="/customer-integration"
        >
          <i class="fal fa-list" />
        </b-button>

        <b-button
          variant="warning"
          class="ml-1 text-white"
          :to="`/customer-integration/${customer_integration.id}/edit`"
        >
          <i class="fal fa-pencil" />
        </b-button>
      </template>
    </default-heading>

    <div class="mt-3" />

    <b-table-lite
      :items="customerIntegrationInfoRows"
      striped
      small
      bordered
      thead-class="d-none"
    >
      <template
        slot="label"
        slot-scope="data"
      >
        <span class="font-weight-bold">{{ data.value }}</span>
      </template>
      <template slot="value" slot-scope="data">
        <span>{{ data.value }}</span>
      </template>
    </b-table-lite>

    <div v-if="customer_integration && customer_integration.config" class="mt-4">
      <h4 class="mb-3">Configuration</h4>
      <b-card>
        <div v-if="customer_integration.config.emails && customer_integration.config.emails.length" class="mb-3">
          <h6 class="font-weight-bold">Email Addresses:</h6>
          <ul class="mb-0">
            <li v-for="email in customer_integration.config.emails" :key="email">
              {{ email }}
            </li>
          </ul>
        </div>

        <div v-if="customer_integration.config.growers && customer_integration.config.growers.length" class="mb-3">
          <h6 class="font-weight-bold">Growers:</h6>
          <ul class="mb-0">
            <li v-for="grower in customer_integration.config.growers" :key="grower">
              {{ grower }}
            </li>
          </ul>
        </div>

        <div v-if="customer_integration.config.require_inspection !== undefined" class="mb-3">
          <span class="font-weight-bold mr-1">Require Inspection:</span>
          <span class="badge" :class="customer_integration.config.require_inspection ? 'badge-success' : 'badge-secondary'">
            {{ customer_integration.config.require_inspection ? 'Yes' : 'No' }}
          </span>
        </div>

        <div v-if="customer_integration.config.require_recommendations !== undefined">
          <span class="font-weight-bold mr-2">Require Recommendations:</span>
          <span class="badge" :class="customer_integration.config.require_recommendations ? 'badge-success' : 'badge-secondary'">
            {{ customer_integration.config.require_recommendations ? 'Yes' : 'No' }}
          </span>
        </div>

        <div v-if="!hasConfigurationData" class="text-muted">
          <i class="fa fa-info-circle"></i> No additional configuration data available.
        </div>
      </b-card>
    </div>

    <div v-else-if="customer_integration" class="mt-4">
      <h4 class="mb-3">Configuration</h4>
      <b-alert show variant="info">
        This integration has no additional configuration.
      </b-alert>
    </div>
  </div>
</template>

<script>
import LoadingOverlay from '@/components/shared/LoadingOverlay'
import DefaultHeading from '@/components/shared/DefaultHeading'
import CustomerIntegrationModel from '@/models/result/CustomerIntegrationModel'
import moment from 'moment'

export default {
  name: "CustomerIntegrationDetail",
  components: {
    LoadingOverlay,
    DefaultHeading
  },
  data () {
    return {
      customer_integration_id: this.$route.params.id || null,
      customer_integration: null,
      loading: true
    }
  },
  computed: {
    customerIntegrationInfoRows () {
      const rows = []

      if (this.customer_integration) {
        rows.push(
          {
            label: 'Status',
            value: this.customer_integration.status || 'Not Specified'
          },
          {
            label: 'Status Note',
            value: this.customer_integration.status_note || 'Not Specified'
          },
          {
            label: 'Last Runtime',
            value: (this.customer_integration.last_runtime_at && moment(this.customer_integration.last_runtime_at).format('YYYY-MM-DD HH:mm:ss')) || 'Not Specified'
          },
          {
            label: 'Last Complete',
            value: (this.customer_integration.last_complete_at && moment(this.customer_integration.last_complete_at).format('YYYY-MM-DD HH:mm:ss')) || 'Not Specified'
          },
          {
            label: 'Created At',
            value: (this.customer_integration.created_at && moment(this.customer_integration.created_at).format('YYYY-MM-DD HH:mm:ss')) || 'Not Specified'
          }
        )
      }

      return rows.filter(row => row.value && row.value !== 'Not Specified')
    },

    hasConfigurationData () {
      if (!this.customer_integration || !this.customer_integration.config) {
        return false
      }

      const config = this.customer_integration.config
      return (
        (config.emails && config.emails.length > 0) ||
        (config.growers && config.growers.length > 0) ||
        config.require_inspection !== undefined ||
        config.require_recommendations !== undefined
      )
    }
  },
  mounted () {
    this.fetchCustomerIntegration()
  },
  methods: {
    async fetchCustomerIntegration () {
      const model = new CustomerIntegrationModel({ id: this.customer_integration_id })

      const params = {}
      model.fetch({ params }).then(() => {
        this.customer_integration = model
        this.loading = false
      }).catch((error) => {
        console.error('Error fetching customer integration:', error)
        this.loading = false
      })
    }
  }
}
</script>
